/**
 * The graph's record, digested once, for every model that is handed a summary
 * of the whole graph.
 *
 * Four prompts summarise the graph: the voice after a pass, the voice after a
 * run, Ask on the seed, and the synthesis scan. Each built its own, and each
 * left out a different part of what the record holds. The run wrap-up saw
 * "Challenge on <label>: 4 nodes" per move and nothing else, so after 24 moves
 * it could not name one dead end or one reading, and guessed wrong about where
 * the dead ends were. The scan saw labels and claims with no reading, and fused
 * through a claim the person's own check had already read against. This is the
 * one list all four read, so a record any of them can see, all of them can.
 *
 * WHAT IS IN IT, in this order: the seed's live survey of the field, when it
 * has one; the dead ends with the reasons given for them; open contradictions;
 * open gaps; takeaways and syntheses (the kind 'insight', read as Takeaway since
 * D333); each source check, with its reading, its
 * date and its search's own counts; each recorded result; panels whose lenses
 * split; and the candidate syntheses waiting in the Overview. Every claim is
 * named by its label in quotes and never by id — the voice and Ask are held to
 * that (D131, D132), and a model copies the form it is shown.
 *
 * The survey's line carries no reading, because a survey has none: it says when
 * and by which model the question was surveyed and what the gate kept and set
 * aside, and never what the field concluded. It is the one record about the
 * question itself, so it leads.
 *
 * EVIDENCE IS WHOLE OR ABSENT. A check's summary and a person's observation
 * enter whole or are said to be withheld, never cut: an arbitrary prefix can
 * keep the effect and drop the "except in…" (the rule `wholeEvidenceText` in
 * `engine/prompts.ts` states for the reasoning context). Claims and reasons are
 * the model's own prose and are clipped, and say so with an ellipsis.
 *
 * Every reading is read through `verificationLive`: a check of wording the
 * claim no longer carries is reported as that, never as the claim's reading.
 *
 * Pure and in `core/`, beside the types it reads: its consumers live in
 * `engine/` and `ui/`, and none of them may own it.
 */

import {
  BEARING_LABEL,
  KIND_LABEL,
  LENS_LABEL,
  LINK_READING_LABEL,
  READING_LABEL,
  readingNote,
  verificationLive,
  type Bridge,
  type BridgeEnd,
  type FieldSurvey,
  type PriorArt,
  type ReasonNode,
  type Session,
  type Verification,
} from './types'
import { ENDING_LABEL, reasonedEndingCounts, reasonedEndingOf } from './endings'
import { panelSplit } from './panel'
import { hostOf } from './url'
import { decodeEntities } from './entities'
import { SURVEY_PARTS, SURVEY_PART_LABEL, surveyLive } from './survey'
import { priorArtTarget } from './priorWork'
import { fmtRating, selfRatingsOf } from './rewrite'
import { CLOSENESS_WORD } from './vocabulary'
import { calendarDay } from './dates'

/**
 * How much of the record one digest carries.
 *
 * `lines` is a cap on item lines, shared across the kinds round-robin so that a
 * graph with forty dead ends still shows its one check. `summaryChars` is the
 * size past which a check summary is withheld rather than entered whole — the
 * same thousand the reasoning context's source records use — and the summaries
 * of one digest together stop at `summaryTotal`, because this digest rides on
 * every Ask turn about the seed, where an unbounded block is money per question.
 * `observedChars` does the same for a person's recorded observation. Claims and
 * dead-end reasons are the model's own prose, clipped at `claimChars`.
 *
 * `surveyTotal` bounds a survey pass's own section (`surveyRecord`): its summary
 * and its rows, each under `summaryChars`, together. A survey written to its
 * brief — a summary of 120 words and sixteen rows of forty — comes to about
 * 5,000 characters, so one that kept to it enters whole, and only one that
 * overran its brief has rows withheld.
 */
export const DIGEST_LIMITS = {
  lines: 16,
  summaryChars: 1000,
  summaryTotal: 3000,
  surveyTotal: 6000,
  observedChars: 600,
  claimChars: 200,
} as const

const clip = (t: string, m: number) => (t.length > m ? `${t.slice(0, m - 1)}…` : t)
const oneLine = (t: string) => t.replace(/\s+/g, ' ').trim()
/**
 * The reader's calendar day, as the prompt's own "Today is" line and every card
 * print it (`calendarDay`, D210). This was the UTC day, so that a record's day
 * could not move between two passes if the reader crossed a time zone — but the
 * same prompt told the pass "Today is" in the reader's calendar, so east of UTC
 * before breakfast a check made ten minutes earlier read to the model as
 * yesterday's, and the narrator could name the 13th beside a card saying the
 * 14th. One clock in the prompt and on the page is the smaller cost; a traveller
 * sees a record's day follow them, as it does on every card.
 */
export const recordDay = (t: number) => calendarDay(t)

/** The seed is named as the seed: its label is the whole typed prompt. */
const nameOf = (n: ReasonNode) => (n.parentId === null ? 'the seed' : `"${n.label}"`)

/* ── A reading, compactly ───────────────────────────────────────────────── */

/**
 * A claim's live reading and the day it was taken, for a line that has room for
 * nothing else — or null when no check describes the words on the claim now.
 * The reasoning context's node lines and its "checked elsewhere" rows both print
 * this, so a reading reads the same wherever a model meets it.
 */
export function readingMark(n: ReasonNode): string | null {
  if (!verificationLive(n)) return null
  const v = n.verification!
  return `reading: ${READING_LABEL[v.reading]}, checked ${recordDay(v.checkedAt)}`
}

/** Every claim carrying a live check, newest check first. */
export function liveChecks(nodes: Record<string, ReasonNode>): ReasonNode[] {
  return Object.values(nodes)
    .filter(verificationLive)
    .sort((a, b) => b.verification!.checkedAt - a.verification!.checkedAt || cmpId(a, b))
}

const cmpId = (a: { id: string }, b: { id: string }) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

/**
 * What the check's search did, in the gate's own counts. Without it the voice saw
 * only "from 0 sources" on a check whose citations had all been set aside, told
 * the person it "read zero sources", and proposed paying for it again — when the
 * search had returned 44 results and the check had named six the gate could not
 * match. Web checks only: a documents-only pass ran no search to count.
 */
export function checkCounts(v: Verification): string {
  if (v.scope === 'corpus') return 'It searched your own documents only.'
  const set = v.dropped
    ? ` ${v.dropped} source${v.dropped === 1 ? '' : 's'} it named ${v.dropped === 1 ? 'was' : 'were'} set aside because the search had not returned ${v.dropped === 1 ? 'it' : 'them'}.`
    : ''
  return `It ran ${v.queries.length} search${v.queries.length === 1 ? '' : 'es'} that returned ${v.seen} result${v.seen === 1 ? '' : 's'}.${set}${v.partial ? ' Some searches did not complete.' : ''}`
}

const CLOSENESS_RANK: Record<PriorArt['closeness'], number> = {
  same: 0,
  overlapping: 1,
  adjacent: 2,
}

/**
 * The closest prior art a check found, by the title of the source it points at.
 *
 * Resolved through the record's own kept sources and cites by `priorArtTarget`,
 * the resolver the card's line uses — `ref` is a surviving source's canonical URL
 * or a cited passage's `passageRef`, and both gates (`reason.ts` live,
 * `persist.ts` off disk) drop an entry whose ref names nothing that survived —
 * so the title printed here is one the search actually returned. An entry that
 * does not resolve is skipped rather than guessed at. This used to read a
 * passage ref's number as a DOCUMENT-level one, which it never was, and name
 * whichever cited document had a passage by that number (D146).
 */
export function closestPriorArt(
  v: Verification,
): { title: string; closeness: PriorArt['closeness'] } | null {
  const ranked = [...v.priorArt].sort(
    (a, b) => CLOSENESS_RANK[a.closeness] - CLOSENESS_RANK[b.closeness],
  )
  for (const art of ranked) {
    const target = priorArtTarget(v, art)
    if (!target) continue
    // A title the provider never supplied stands in as the URL; the host says
    // the same thing in fewer characters. A passage has a document title always.
    const title =
      target.passage === undefined && target.url && target.title === target.url
        ? hostOf(target.url)
        : target.title
    return { title: decodeEntities(title), closeness: art.closeness }
  }
  return null
}

/* ── What one check disputed ────────────────────────────────────────────── */

/**
 * The least number of kept sources that must dispute a claim before a mixed
 * reading counts as the check disputing it.
 */
export const DISPUTED_MIN_SOURCES = 1

/**
 * When a claim counts as one a check disputed — the mechanical rule behind the
 * mark on an Overview candidate and the note on the digest's candidate
 * lines, in the words a hover prints.
 *
 * `refuted` always counts. A mixed reading counts when its kept sources lean
 * against the claim: at least `DISPUTED_MIN_SOURCES` disputing it and no more
 * supporting it than disputing it. A mixed reading whose sources all support
 * part of the claim is not a dispute, and one with a lone dissent among four
 * supports is a record of disagreement, not of the claim being read against.
 * The stances are the model's labels on sources the citation gate admitted —
 * the same counts `readingNote` prints under a mixed reading.
 */
export const DISPUTED_RULE = `a live source check read it as “${READING_LABEL.refuted}”, or as a “${READING_LABEL.mixed}” with at least ${DISPUTED_MIN_SOURCES} kept source disputing it and no more supporting it than disputing it`

/** Whether a claim's live check disputes it, by `DISPUTED_RULE`. */
export function checkDisputes(n: ReasonNode): boolean {
  if (!verificationLive(n)) return false
  const v = n.verification!
  if (v.reading === 'refuted') return true
  if (v.reading !== 'mixed') return false
  const disputes = v.sources.filter((s) => s.stance === 'disputes').length
  const supports = v.sources.filter((s) => s.stance === 'supports').length
  return disputes >= DISPUTED_MIN_SOURCES && disputes >= supports
}

/**
 * The claims beneath a candidate's local ends that a check disputed, nearest
 * first: each end itself, then what it derives from — its parent and, for a
 * synthesis, its second parent — breadth first.
 *
 * The seed is not walked. Everything on the graph derives from it, so a check
 * that disputed the seed would mark every candidate at once, and a mark on
 * every card tells a reader nothing about any one of them.
 */
export function restsOnDisputed(nodes: Record<string, ReasonNode>, b: Bridge): ReasonNode[] {
  const out: ReasonNode[] = []
  const seen = new Set<string>()
  const queue = [b.a, b.b].flatMap((e) => (e.where === 'local' ? [e.nodeId] : []))
  while (queue.length) {
    const id = queue.shift()!
    if (seen.has(id)) continue
    seen.add(id)
    const n = nodes[id]
    if (!n || n.parentId === null) continue
    if (checkDisputes(n)) out.push(n)
    queue.push(n.parentId, ...(n.bridgeFrom ?? []))
  }
  return out
}

/* ── The digest ─────────────────────────────────────────────────────────── */

export interface DigestOptions {
  /** Listed first within each kind — a run's own nodes, so its wrap-up reads
   *  its own work before the rest of the graph. */
  focus?: ReadonlySet<string>
  /** Left out: node or candidate ids a pass's digest already prints in full,
   *  and a survey by the id of the op that ran it. */
  exclude?: ReadonlySet<string>
}

interface Row {
  id: string
  /** Within a kind, after the focus: a lower tier first — a live reading ahead
   *  of a note that a reading no longer applies. */
  tier?: number
  /** Newest first within a tier. */
  at: number
  /** Rendered late, so the summary budget is spent in display order. */
  text: (budget: { left: number }) => string
}

interface Group {
  /** Singular and plural, for the line that counts what was left out. */
  noun: [string, string]
  rows: Row[]
  /** Lines this kind takes per round of the cap; one unless said otherwise. */
  share?: number
}

/**
 * A summary whole, or a sentence saying it was withheld — never a prefix. A
 * check's and a survey's alike: both carry the codec's record of what it clipped,
 * and a summary it clipped is not repeated at all. `whose` names the model whose
 * words these are, and `card` where the whole text can be read.
 */
function wholeSummary(
  record: Pick<Verification | FieldSurvey, 'summary' | 'textTruncations'>,
  budget: { left: number },
  whose = 'the check model’s',
  card = 'the claim’s card',
): string {
  if (!record.summary) return ''
  if (record.textTruncations?.some((t) => t.field === 'summary'))
    return ' Its summary was not retained whole, so none of it is repeated here.'
  const text = oneLine(record.summary)
  if (text.length > DIGEST_LIMITS.summaryChars || text.length > budget.left)
    return ` Its summary is withheld here as a whole, for length; it is on ${card}.`
  budget.left -= text.length
  return ` Its summary, in ${whose} words: ${text}`
}

/** The reading's own note where the label alone is misread — the stance counts
 *  under a mixed reading, the weakness of an absence, which of two reasons a check
 *  could not check the claim (D326) — and nothing where the label says it all. An
 *  unsourced check's set-aside count is already in `checkCounts`, so only the half
 *  that stops "it found nothing" is added. */
function noteFor(v: Verification): string {
  switch (v.reading) {
    case 'supported':
      return ''
    case 'unsourced':
      return ' That is not the same as finding nothing.'
    case 'mixed':
    case 'refuted':
    case 'nothing-found':
    case 'not-checkable':
      return ` ${readingNote(v)}`
  }
}

function priorArtLine(n: ReasonNode, v: Verification): string {
  const art = closestPriorArt(v)
  if (!art) return ''
  const how = CLOSENESS_WORD[art.closeness]
  // The model's own rating only where it rated these words: a rewrite keeps a
  // number that rated the claim it replaced, and a carried node has one nobody
  // gave (`selfRatingsOf`). The card's prior-work line drops it the same way.
  const own = selfRatingsOf(n)
  const moved =
    v.measuredNovelty === undefined
      ? ' Novelty was not re-rated.'
      : own
        ? ` It re-rated novelty to ${fmtRating(v.measuredNovelty)}, against the model’s own ${fmtRating(own.novelty)} before searching; both are the model’s ratings.`
        : ` It rated novelty ${fmtRating(v.measuredNovelty)}; that is the checking model’s rating.`
  return ` Prior art: the closest work it found is "${oneLine(art.title)}" (${how}).${moved}`
}

function checkRow(n: ReasonNode): Row {
  const v = n.verification!
  if (!verificationLive(n))
    return {
      id: n.id,
      tier: 1,
      at: v.checkedAt,
      text: () =>
        `Source check on ${nameOf(n)}, ${recordDay(v.checkedAt)}: it read an earlier wording, and the claim has been rewritten since, so no reading describes it now.`,
    }
  return {
    id: n.id,
    at: v.checkedAt,
    text: (budget) =>
      `Source check on ${nameOf(n)}, ${recordDay(v.checkedAt)}: ${READING_LABEL[v.reading]}.${noteFor(v)} ${checkCounts(v)}${priorArtLine(n, v)}${wholeSummary(v, budget)}`,
  }
}

const endName = (e: BridgeEnd, nodes: Record<string, ReasonNode>): string => {
  if (e.where === 'foreign') return `"${e.ref.label}" from your exploration of “${e.ref.seed}”`
  const n = nodes[e.nodeId]
  return n ? nameOf(n) : 'a claim since pruned'
}

/* ── The survey of the field ────────────────────────────────────────────── */

const SURVEY_WHOSE = 'the surveying model’s'
const SEED_CARD = 'the seed’s card'

const count = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`

/**
 * What a survey's search did and what the gate kept, in its own counts — the
 * survey's `checkCounts`, for that function's reason: a survey whose every URL
 * was set aside must not reach the voice as a search that found nothing. Counts
 * only, and no reading word, because a survey has no reading to report.
 */
export function surveyCounts(sv: FieldSurvey): string {
  const kept = sv.sources.length
    ? `It kept ${count(sv.sources.length, 'returned source', 'returned sources')}.`
    : 'It kept no returned source, so none of its prose was kept either. That is not the same as finding nothing.'
  const urls = sv.dropped
    ? ` ${count(sv.dropped, 'URL', 'URLs')} it named ${sv.dropped === 1 ? 'was' : 'were'} set aside because the search had not returned ${sv.dropped === 1 ? 'it' : 'them'}.`
    : ''
  const rows = sv.rowsSetAside
    ? ` ${count(sv.rowsSetAside, 'row', 'rows')} ${sv.rowsSetAside === 1 ? 'was' : 'were'} set aside with no returned source behind ${sv.rowsSetAside === 1 ? 'it' : 'them'}.`
    : ''
  return `It ran ${count(sv.searches, 'search', 'searches')} that returned ${count(sv.seen, 'result', 'results')}. ${kept}${urls}${rows}${sv.partial ? ' Some searches did not complete.' : ''}`
}

/**
 * The digest's line for the seed's live survey: dated, stamped, the gate's
 * counts, and the summary whole or withheld under the same budget as a check's.
 * No rows — a whole-graph line has room for what the survey IS, and the survey
 * pass's own section (`surveyRecord`) is where its rows are read.
 */
function surveyRow(sv: FieldSurvey): Row {
  return {
    id: sv.opId,
    at: sv.surveyedAt,
    text: (budget) =>
      `Survey of the field around the seed question, ${recordDay(sv.surveyedAt)}, by ${sv.model.name}: a record of what one search returned about the question, not an answer to it. ${surveyCounts(sv)}${wholeSummary(sv, budget, SURVEY_WHOSE, SEED_CARD)}`,
  }
}

/**
 * A survey pass's own record, for the voice's section on that pass: the date,
 * the model and the gate's counts, then the summary and the rows by part, each
 * entered whole or withheld whole within `DIGEST_LIMITS.surveyTotal` — never a
 * prefix, for `wholeSummary`'s reason. A row's study design is a label, like a
 * source's title, and rides beside it.
 *
 * The codec records a clipped row by its index at the boundary that clipped it,
 * and rows dropped on either side of that boundary shift the index, so the record
 * cannot say WHICH row was clipped. Where any was, none is repeated.
 */
export function surveyRecord(sv: FieldSurvey): string {
  const budget = { left: DIGEST_LIMITS.surveyTotal }
  const out = [`Surveyed ${recordDay(sv.surveyedAt)} by ${sv.model.name}. ${surveyCounts(sv)}`]
  const summary = wholeSummary(sv, budget, SURVEY_WHOSE, SEED_CARD).trim()
  if (summary) out.push(summary)
  if (sv.textTruncations?.some((t) => t.field.startsWith('rows['))) {
    out.push(
      'Its rows were not all retained whole, and the record does not say which, so none of them is repeated here; they are on the seed’s card.',
    )
    return out.join('\n')
  }
  for (const part of SURVEY_PARTS) {
    const rows = sv.rows.filter((r) => r.part === part)
    if (!rows.length) continue
    out.push(`${SURVEY_PART_LABEL[part]}:`)
    for (const r of rows) {
      const text = oneLine(r.text)
      if (text.length > DIGEST_LIMITS.summaryChars || text.length > budget.left) {
        out.push('- One row is withheld here as a whole, for length; it is on the seed’s card.')
        continue
      }
      budget.left -= text.length
      out.push(`- ${text}${r.design ? ` (study design: ${oneLine(r.design)})` : ''}`)
    }
  }
  return out.join('\n')
}

/**
 * The graph's record as item lines, or '' when nothing has been derived yet and
 * the seed carries no live survey.
 *
 * The first line counts the whole graph — claims, live checks, recorded
 * results, dead ends — so a reader of a capped list can still tell "three of
 * forty claims were checked" from "three claims, all checked". The item lines
 * are shared across the kinds in rounds, so every kind present is shown before
 * any kind gets a second turn. A check and a recorded result take two lines a
 * round: they are the records that came from outside the model, each cost a
 * search or an experiment, and they are what the summaries kept losing. What the
 * cap left out is counted by kind on the last line, never silently dropped.
 *
 * The seed's live survey is a kind of its own, first, and takes its line from
 * the same cap. A seed surveyed before anything was derived under it still has
 * a record to digest — the tally then counts no claims, and says so.
 */
export function recordDigest(
  s: Pick<Session, 'nodes' | 'bridges'>,
  opts: DigestOptions = {},
): string {
  const focus = opts.focus ?? new Set<string>()
  const exclude = opts.exclude ?? new Set<string>()
  const all = Object.values(s.nodes)
  const derived = all.filter((n) => n.parentId !== null)
  // Live only: a survey of wording the seed no longer carries is a survey of a
  // different question, and printed here it would read as this one's.
  const seed = all.find((n) => n.parentId === null)
  const survey = seed && surveyLive(seed) && !exclude.has(seed.survey!.opId) ? seed.survey! : null
  if (!derived.length && !survey) return ''
  const kept = all.filter((n) => !exclude.has(n.id))
  const byNew = (n: ReasonNode) => n.createdAt
  const node = (n: ReasonNode, text: string): Row => ({ id: n.id, at: byNew(n), text: () => text })
  const claimOf = (n: ReasonNode) => clip(oneLine(n.claim), DIGEST_LIMITS.claimChars)

  const groups: Group[] = [
    {
      noun: ['survey of the field', 'surveys of the field'],
      rows: survey ? [surveyRow(survey)] : [],
    },
    {
      noun: ['ending', 'endings'],
      rows: kept
        .filter((n) => n.terminal && n.parentId !== null)
        .map((n) => {
          const why = n.terminalReason ? oneLine(n.terminalReason) : ''
          // An objection that stood says what it stood against, as an open
          // contradiction's line does. Without it a run's wrap-up — which reads
          // only this digest (D51) — was told an objection stood and not which
          // claim it held against, so it could not say which of the claims did
          // not survive the argument. A fact, not an instruction: the voice's
          // rules on what it may call refuted are unchanged.
          const parent =
            n.terminalKind !== 'constraint' && reasonedEndingOf(n) === 'objection-stood'
              ? s.nodes[n.parentId!]
              : undefined
          return node(
            n,
            `${n.terminalKind === 'constraint' ? 'Closed only by a constraint the person set' : reasonedEndingOf(n) === 'open' ? `Left open by the model's reasoning (${ENDING_LABEL.open})` : `Ended in the model's reasoning (${ENDING_LABEL[reasonedEndingOf(n) ?? 'path-failed']})`}: ${nameOf(n)}${parent ? `, against ${nameOf(parent)}` : ''}${why ? ` — ${clip(why, DIGEST_LIMITS.claimChars)}` : ''}`,
          )
        }),
    },
    {
      noun: ['open contradiction', 'open contradictions'],
      rows: kept
        .filter((n) => n.kind === 'contradiction' && !n.terminal)
        .map((n) => {
          const parent = n.parentId ? s.nodes[n.parentId] : undefined
          return node(
            n,
            `Open contradiction: ${nameOf(n)}${parent ? `, against ${nameOf(parent)}` : ''} — ${claimOf(n)}`,
          )
        }),
    },
    {
      noun: ['open gap', 'open gaps'],
      rows: kept
        .filter((n) => n.kind === 'gap' && !n.terminal)
        .map((n) => node(n, `Open gap: ${nameOf(n)} — ${claimOf(n)}`)),
    },
    {
      noun: ['takeaway or synthesis', 'takeaways and syntheses'],
      rows: kept
        .filter((n) => (n.kind === 'insight' || n.kind === 'synthesis') && !n.terminal)
        .map((n) => node(n, `${KIND_LABEL[n.kind]}: ${nameOf(n)} — ${claimOf(n)}`)),
    },
    {
      noun: ['source check', 'source checks'],
      rows: kept.filter((n) => n.verification).map(checkRow),
      share: 2,
    },
    {
      noun: ['recorded result', 'recorded results'],
      share: 2,
      rows: kept
        .filter((n) => n.outcome)
        .map((n) => {
          const o = n.outcome!
          const observed = oneLine(o.observed)
          return {
            id: n.id,
            at: o.recordedAt,
            text: () =>
              `Result the person recorded on ${nameOf(n)}, observed ${recordDay(o.at)}: ${BEARING_LABEL[o.bearing]}. ${
                observed.length <= DIGEST_LIMITS.observedChars
                  ? `What they observed: ${observed}`
                  : 'What they observed is withheld here as a whole, for length; it is on the claim’s card.'
              }`,
          }
        }),
    },
    {
      noun: ['panel split', 'panel splits'],
      rows: kept
        .filter((n) => panelSplit(n.panel))
        .map((n) => ({
          id: n.id,
          at: n.panel!.checkedAt,
          text: () =>
            `Three lenses split on ${nameOf(n)}: ${n.panel!.votes.map((v) => `${LENS_LABEL[v.lens]} ${v.verdict.replace('-', ' ')}`).join(', ')}.`,
        })),
    },
    {
      noun: ['candidate synthesis', 'candidate syntheses'],
      rows: s.bridges
        .filter((b) => !b.realizedNodeId && !exclude.has(b.id))
        .map((b) => {
          const beneath = restsOnDisputed(s.nodes, b)
          return {
            id: b.id,
            at: b.createdAt,
            text: () =>
              `Candidate synthesis waiting in the Overview, not realised: "${oneLine(b.title)}", joining ${endName(b.a, s.nodes)} and ${endName(b.b, s.nodes)}.${
                b.linkage
                  ? ` A search for work joining them read: ${LINK_READING_LABEL[b.linkage.reading]}.`
                  : ''
              }${beneath.length ? ` It rests on a claim a check disputed: ${nameOf(beneath[0]!)}.` : ''}`,
          }
        }),
    },
  ]

  // Focus first, then tier, then newest, then id — a total order, so the same
  // graph always digests to the same bytes.
  const rank = (r: Row) => (focus.has(r.id) ? 0 : 1)
  for (const g of groups)
    g.rows.sort(
      (a, b) => rank(a) - rank(b) || (a.tier ?? 0) - (b.tier ?? 0) || b.at - a.at || cmpId(a, b),
    )

  // Round-robin: each kind its share of lines per round until the cap, so no
  // kind is starved by a long one ahead of it in the order.
  const shown = groups.map(() => 0)
  let used = 0
  for (let more = true; more && used < DIGEST_LIMITS.lines;) {
    more = false
    for (let i = 0; i < groups.length && used < DIGEST_LIMITS.lines; i++) {
      const g = groups[i]!
      const take = Math.min(g.share ?? 1, g.rows.length - shown[i]!, DIGEST_LIMITS.lines - used)
      if (take > 0) {
        shown[i]! += take
        used += take
        more = true
      }
    }
  }

  const budget = { left: DIGEST_LIMITS.summaryTotal }
  const lines = groups.flatMap((g, i) =>
    g.rows.slice(0, shown[i]).map((r) => `- ${r.text(budget)}`),
  )
  const left = groups
    .map((g, i) => ({ g, n: g.rows.length - shown[i]! }))
    .filter(({ n }) => n > 0)
    .map(({ g, n }) => `${n} more ${g.noun[n === 1 ? 0 : 1]}`)

  const live = derived.filter(verificationLive)
  const checked = live.length
  // Every live check's reading, counted, so a voice reading a capped list cannot
  // take the checks it was shown for all of them. A live measurement found wrap-
  // ups saying "the four checks" of an exploration that had seven.
  const readingCounts = new Map<string, number>()
  for (const n of live) {
    const label = READING_LABEL[n.verification!.reading].toLowerCase()
    readingCounts.set(label, (readingCounts.get(label) ?? 0) + 1)
  }
  const readings = [...readingCounts].map(([label, k]) => `${k} ${label}`).join(', ')
  const results = derived.filter((n) => n.outcome).length
  // A path the person's own constraint closed is not an ending the model reasoned
  // to, so it is counted apart rather than filed as a failed path.
  const reasoned = derived.filter((n) => n.terminal && n.terminalKind !== 'constraint')
  const constrained = derived.filter((n) => n.terminal && n.terminalKind === 'constraint').length
  const dead = reasoned.length
  // Counted by the ending the model reasoned to, so an open question is never
  // folded into a count the voice will read as closed paths.
  const endings = reasonedEndingCounts(reasoned)
  const tally = `${derived.length} claim${derived.length === 1 ? '' : 's'} under the seed: ${checked} carr${checked === 1 ? 'ies' : 'y'} a live source check${checked ? ` (${checked === 1 ? 'reading' : 'readings'}: ${readings})` : ''}, ${results} a recorded result, and the model's reasoning reached an ending on ${dead}${endings ? ` (${endings})` : ''}${constrained ? `; ${constrained} ${constrained === 1 ? 'was' : 'were'} closed by a constraint the person set` : ''}.`

  return [
    tally,
    ...(lines.length ? lines : ['- Nothing on the graph is closed, open, checked or split yet.']),
    ...(left.length ? [`Not listed, for length: ${left.join(', ')}.`] : []),
  ].join('\n')
}
